import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Home, Building2, CreditCard, Wallet, PiggyBank, 
  User, CheckCircle2, ArrowLeft, ArrowRight, Shield, 
  TrendingUp, Calculator, Phone, Mail, Lock, Users,
  ChevronLeft, ChevronRight, Clock, Flame
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL from assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_f9e37d31-967b-4854-b567-b2784bb2ed6c/artifacts/ya50dq4o_%D7%9C%D7%95%D7%92%D7%95%20%D7%A9%D7%97%D7%95%D7%A8.jpg";

// ===== META PIXEL HELPERS =====
const initMetaPixels = (pixelIds) => {
  if (!pixelIds || pixelIds.length === 0) return;
  
  // Load Facebook Pixel script if not already loaded
  if (!window.fbq) {
    (function(f,b,e,v,n,t,s) {
      if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s);
    })(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
  }
  
  // Initialize all pixels
  pixelIds.forEach(pixelId => {
    window.fbq('init', pixelId);
  });
};

const trackPageView = () => {
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
};

const trackLead = () => {
  if (window.fbq) {
    window.fbq('track', 'Lead');
  }
};

// Format number with commas (every 3 digits)
const formatNumber = (num) => {
  if (!num && num !== 0) return "";
  // Remove all non-digits first, then format
  const cleanNum = String(num).replace(/[^\d]/g, "");
  if (!cleanNum) return "";
  return parseInt(cleanNum, 10).toLocaleString("he-IL");
};

// Parse formatted number
const parseNumber = (str) => {
  if (!str) return 0;
  return parseFloat(str.replace(/,/g, "")) || 0;
};

// Calculate new monthly payment using amortization formula
const calculateNewPayment = (totalDebt, annualRate, months) => {
  if (totalDebt <= 0 || annualRate <= 0 || months <= 0) return 0;
  const monthlyRate = annualRate / 12;
  const payment = (totalDebt * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
  return Math.round(payment);
};

// Calculate future value (compound interest)
const calculateFutureValue = (monthlySaving, annualReturn, years) => {
  if (monthlySaving <= 0 || annualReturn <= 0 || years <= 0) return 0;
  const monthlyRate = annualReturn / 12;
  const months = years * 12;
  const fv = monthlySaving * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
  return Math.round(fv);
};

// Get interest rate based on BDI
const getInterestRate = (bdi) => {
  switch (bdi) {
    case "excellent": return 0.055;
    case "medium": return 0.075;
    case "poor": return 0.105;
    default: return 0.065;
  }
};

const LoanCalculator = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pixelsLoaded, setPixelsLoaded] = useState(false);
  const [questions, setQuestions] = useState({});
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes in seconds
  const pixelsInitialized = useRef(false);
  const [formData, setFormData] = useState({
    // Step 1: Property ownership
    asset: "",
    // Step 2: Property value
    asset_value: "",
    // Step 3: Existing mortgage
    mortage: "",
    mortage_monthly: "",
    // Step 4: Total debts
    loans: "",
    // Step 5: Current monthly payment
    loans_monthly: "",
    // Step 6: BDI
    bdi: "",
    // Step 7: Income
    income: "",
    // Step 8: Contact
    name: "",
    phone: "",
    email: "",
    // UTM params
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_content: "",
    utm_term: "",
  });
  const [results, setResults] = useState({
    currentPayment: 0,
    newPayment: 0,
    monthlySavings: 0,
    fv5: 0,
    fv10: 0,
    fv20: 0,
  });

  // Extract UTM params from URL on load and load pixels + questions
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setFormData(prev => ({
      ...prev,
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_content: params.get("utm_content") || "",
      utm_term: params.get("utm_term") || "",
    }));
    
    // Generate session ID
    if (!sessionStorage.getItem("momento_session")) {
      sessionStorage.setItem("momento_session", Math.random().toString(36).slice(2) + Date.now());
    }
    
    // Load pixels from API
    const loadPixels = async () => {
      try {
        const res = await axios.get(`${API}/pixels`);
        if (res.data.pixels && res.data.pixels.length > 0 && !pixelsInitialized.current) {
          initMetaPixels(res.data.pixels);
          pixelsInitialized.current = true;
          setPixelsLoaded(true);
          trackPageView();
        }
      } catch (err) {
        console.error("Error loading pixels:", err);
      }
    };
    loadPixels();
    
    // Load custom questions from API
    const loadQuestions = async () => {
      try {
        const res = await axios.get(`${API}/questions`);
        setQuestions(res.data);
      } catch (err) {
        console.error("Error loading questions:", err);
      }
    };
    loadQuestions();
  }, []);

  // Track page view + step tracking on step change
  useEffect(() => {
    if (pixelsLoaded && currentStep > 0) {
      trackPageView();
    }
    
    if (currentStep > 0) {
      const stepNames = {
        1: "בעלות על נכס",
        2: "שווי הנכס",
        3: "משכנתא קיימת",
        4: "סך ההלוואות",
        5: "החזר חודשי נוכחי",
        6: "היסטוריית אשראי",
        7: "הכנסת משק הבית",
        8: "פרטי קשר"
      };
      
      // Push to dataLayer for GTM
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'questionnaire_step',
          step_number: currentStep,
          step_name: stepNames[currentStep] || `שלב ${currentStep}`,
        });
      }
      
      // Track step visit in backend
      const sessionId = sessionStorage.getItem("momento_session") || "";
      axios.post(`${API}/track/step`, {
        step_number: currentStep,
        step_name: stepNames[currentStep] || `שלב ${currentStep}`,
        session_id: sessionId
      }).catch(err => console.error("Step tracking error:", err));
    }
  }, [currentStep, pixelsLoaded]);

  // Countdown timer for step 8 offer
  useEffect(() => {
    if (currentStep === 8 && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentStep, timeLeft]);

  const totalSteps = 9; // Landing + 8 steps

  const handleNext = () => {
    if (currentStep === 0) {
      setCurrentStep(1);
      return;
    }

    // Validation
    if (currentStep === 1 && formData.asset !== "yes") {
      toast.error("הפתרון שלנו מבוסס על נכס קיים");
      return;
    }
    if (currentStep === 2 && parseNumber(formData.asset_value) < 500000) {
      toast.error("אנא הזינו שווי נכס תקין");
      return;
    }
    if (currentStep === 4 && parseNumber(formData.loans) < 50000) {
      toast.error("סכום החובות צריך להיות לפחות 50,000 ₪");
      return;
    }
    if (currentStep === 5 && parseNumber(formData.loans_monthly) < 500) {
      toast.error("אנא הזינו את ההחזר החודשי הנוכחי");
      return;
    }
    if (currentStep === 6 && !formData.bdi) {
      toast.error("אנא בחרו את מצב האשראי שלכם");
      return;
    }
    if (currentStep === 7 && parseNumber(formData.income) < 5000) {
      toast.error("אנא הזינו הכנסה חודשית תקינה");
      return;
    }
    // After step 7, calculate preview for step 8
    if (currentStep === 7) {
      const totalDebt = parseNumber(formData.loans) + parseNumber(formData.mortage);
      const currentPayment = parseNumber(formData.loans_monthly) + parseNumber(formData.mortage_monthly);
      const interestRate = getInterestRate(formData.bdi);
      const newPayment = calculateNewPayment(totalDebt, interestRate, 240);
      const monthlySavings = Math.max(0, currentPayment - newPayment);
      setResults({
        currentPayment,
        newPayment,
        monthlySavings,
        fv5: calculateFutureValue(monthlySavings, 0.07, 5),
        fv10: calculateFutureValue(monthlySavings, 0.07, 10),
        fv20: calculateFutureValue(monthlySavings, 0.07, 20),
      });
    }
    if (currentStep === 8) {
      if (!formData.name || formData.name.length < 2) {
        toast.error("אנא הזינו שם מלא");
        return;
      }
      if (!formData.phone || formData.phone.length < 9) {
        toast.error("אנא הזינו מספר טלפון תקין");
        return;
      }
      // Calculate and show results
      calculateResults();
      return;
    }

    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const calculateResults = async () => {
    setIsProcessing(true);

    const totalDebt = parseNumber(formData.loans) + parseNumber(formData.mortage);
    const currentPayment = parseNumber(formData.loans_monthly) + parseNumber(formData.mortage_monthly);
    const interestRate = getInterestRate(formData.bdi);
    
    // Calculate new payment (20 year term = 240 months)
    const newPayment = calculateNewPayment(totalDebt, interestRate, 240);
    const monthlySavings = Math.max(0, currentPayment - newPayment);

    // Calculate future values at 7% annual return
    const fv5 = calculateFutureValue(monthlySavings, 0.07, 5);
    const fv10 = calculateFutureValue(monthlySavings, 0.07, 10);
    const fv20 = calculateFutureValue(monthlySavings, 0.07, 20);

    setResults({
      currentPayment,
      newPayment,
      monthlySavings,
      fv5,
      fv10,
      fv20,
    });

    // Submit to API
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        asset: formData.asset,
        asset_value: parseNumber(formData.asset_value),
        mortage: parseNumber(formData.mortage),
        mortage_monthly: parseNumber(formData.mortage_monthly),
        loans: parseNumber(formData.loans),
        loans_monthly: parseNumber(formData.loans_monthly),
        income: parseNumber(formData.income),
        bdi: formData.bdi,
        back: formData.bdi,
        lapoal: "",
        age: "",
        family_status: "",
        income_status: "",
        important: "",
        important_other: "",
        utm_source: formData.utm_source,
        utm_medium: formData.utm_medium,
        utm_campaign: formData.utm_campaign,
        utm_content: formData.utm_content,
        utm_term: formData.utm_term,
        new_payment: newPayment,
        monthly_savings: monthlySavings,
        future_value_5y: fv5,
        future_value_10y: fv10,
        future_value_20y: fv20,
      });
    } catch (err) {
      console.error("Submit error:", err);
    }

    // Store results in localStorage for results page (no PII in URL)
    localStorage.setItem("momento_current", currentPayment);
    localStorage.setItem("momento_new", newPayment);
    localStorage.setItem("momento_savings", monthlySavings);
    localStorage.setItem("momento_fv5", fv5);
    localStorage.setItem("momento_fv10", fv10);
    localStorage.setItem("momento_fv20", fv20);
    localStorage.setItem("momento_name", formData.name);
    localStorage.setItem("momento_phone", formData.phone);

    // Show processing animation then navigate to results page
    setTimeout(() => {
      setIsProcessing(false);
      // Navigate to separate results page - PII kept in localStorage, not URL
      navigate("/results");
    }, 3500);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Helper to get question text from custom or default
  const getQ = (stepNum, field) => {
    return questions[stepNum]?.[field] || "";
  };

  // Format countdown timer as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const pageVariants = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
  };

  // Landing Page
  const renderLanding = () => (
    <motion.div
      key="landing"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen flex flex-col"
      data-testid="landing-page"
    >
      {/* Header */}
      <header className="bg-[#0F172A] py-3 px-3">
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <img src={LOGO_URL} alt="MOMENTO" className="h-12 md:h-20 object-contain" />
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-1 bg-gradient-to-b from-[#F8FAFC] to-white overflow-x-hidden">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-6 md:py-12">
          {/* Main Headline */}
          <div className="text-center mb-6">
            <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-3 leading-tight px-1">
              במקום <span className="text-[#EF4444]">"לכבות שריפות"</span> כל חודש
            </h1>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-tight px-1">
              <span className="gold-text">תנו לנכס שלכם לייצר לכם הון עתידי</span>
            </h2>
            
            <p className="text-base sm:text-lg md:text-xl text-[#475569] mt-4 font-medium px-2">
              בעלי נכס עם התחייבויות מעל 300,000 ₪?
            </p>
            
            <p className="text-sm sm:text-base md:text-lg text-[#64748B] mt-3 max-w-2xl mx-auto leading-relaxed px-2">
              האלגוריתם שלנו יחשב איך להוריד את ההחזר החודשי ב-50% לפחות,
              ולהפוך את ההפרש לחיסכון של מיליונים למשפחה.
            </p>
          </div>

          {/* Benefits */}
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 mb-6 border border-[#E2E8F0] mx-1">
            <div className="space-y-3">
              <div className="flex items-start gap-2 sm:gap-3">
                <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-[#10B981] flex-shrink-0 mt-0.5" />
                <span className="text-[#334155] text-sm sm:text-base md:text-lg">בדיקה ללא עלות וללא רישום ב-BDI</span>
              </div>
              <div className="flex items-start gap-2 sm:gap-3">
                <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-[#10B981] flex-shrink-0 mt-0.5" />
                <span className="text-[#334155] text-sm sm:text-base md:text-lg">חיסכון מיידי: צמצום ההחזר החודשי באלפי שקלים</span>
              </div>
              <div className="flex items-start gap-2 sm:gap-3">
                <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-[#10B981] flex-shrink-0 mt-0.5" />
                <span className="text-[#334155] text-sm sm:text-base md:text-lg">ראייה לעתיד: גלו כמה כסף תוכלו לצבור אם תשקיעו את החיסכון</span>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mb-6 px-2">
            <Button
              onClick={handleNext}
              className="w-full sm:w-auto h-14 sm:h-16 px-6 sm:px-10 text-base sm:text-lg md:text-xl font-bold bg-[#C5A45A] hover:bg-[#B8953D] text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
              data-testid="start-button"
            >
              גלו כמה כסף הבית שלכם יכול לשחרר
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
            </Button>
          </div>

          {/* Trust Elements */}
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-8 text-xs sm:text-sm text-[#64748B] px-2">
            <div className="flex items-center gap-1 sm:gap-2">
              <Lock className="w-3 h-3 sm:w-4 sm:h-4 text-[#10B981]" />
              <span>מאובטח בתקן SSL</span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-[#10B981]" />
              <span>בדיקה אנונימית</span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 text-[#10B981]" />
              <span>עזרנו ל-160 משפחות ב-2025</span>
            </div>
          </div>
        </div>
      </main>
    </motion.div>
  );

  // Step 1: Property Ownership
  const renderStep1 = () => (
    <motion.div
      key="step1"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4 sm:space-y-6"
      data-testid="step-1"
    >
      <div className="text-center mb-6">
        <Building2 className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-[#C5A45A] mb-3" />
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0F172A]">{getQ(1, 'title') || "בדיקת התאמה ראשונית"}</h2>
        <p className="text-sm sm:text-base text-[#64748B] mt-2">{getQ(1, 'subtitle') || "האם יש בבעלותכם נכס נדל\"ן בישראל?"}</p>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <button
          onClick={() => {
            updateField("asset", "yes");
            setCurrentStep(2);
          }}
          className={`w-full p-4 sm:p-6 rounded-xl border-2 transition-all text-right ${
            formData.asset === "yes"
              ? "border-[#C5A45A] bg-[#C5A45A]/10"
              : "border-[#E2E8F0] hover:border-[#C5A45A]/50"
          }`}
          data-testid="asset-yes"
        >
          <span className="text-lg sm:text-xl font-semibold text-[#0F172A]">כן, יש לי נכס</span>
        </button>
        
        <button
          onClick={() => {
            updateField("asset", "no");
            toast.info("הפתרון שלנו מבוסס על איחוד הלוואות כנגד נכס קיים. נשמח לעזור בפתרונות אחרים!");
          }}
          className={`w-full p-4 sm:p-6 rounded-xl border-2 transition-all text-right ${
            formData.asset === "no"
              ? "border-[#EF4444] bg-[#EF4444]/10"
              : "border-[#E2E8F0] hover:border-[#64748B]"
          }`}
          data-testid="asset-no"
        >
          <span className="text-lg sm:text-xl font-semibold text-[#0F172A]">לא</span>
        </button>
      </div>
    </motion.div>
  );

  // Step 2: Property Value
  const renderStep2 = () => (
    <motion.div
      key="step2"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4 sm:space-y-6"
      data-testid="step-2"
    >
      <div className="text-center mb-6">
        <Home className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-[#C5A45A] mb-3" />
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0F172A]">{getQ(2, 'title') || "שווי הנכס"}</h2>
        <p className="text-sm sm:text-base text-[#64748B] mt-2">{getQ(2, 'subtitle') || "מהו שווי הנכס המוערך שלכם כיום?"}</p>
        <p className="text-xs sm:text-sm text-[#94A3B8] mt-1">{getQ(2, 'hint') || "הערכה גסה מספיקה כרגע"}</p>
      </div>

      <div className="relative">
        <Input
          type="text"
          inputMode="numeric"
          placeholder="לדוגמה: 2,000,000"
          value={formatNumber(formData.asset_value)}
          onChange={(e) => updateField("asset_value", e.target.value.replace(/[^\d]/g, ""))}
          className="h-14 sm:h-16 text-xl sm:text-2xl text-center pr-4 pl-12 font-mono"
          data-testid="asset-value-input"
        />
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl sm:text-2xl text-[#64748B]">₪</span>
      </div>
    </motion.div>
  );

  // Step 3: Existing Mortgage
  const renderStep3 = () => (
    <motion.div
      key="step3"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4 sm:space-y-6"
      data-testid="step-3"
    >
      <div className="text-center mb-6">
        <CreditCard className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-[#C5A45A] mb-3" />
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0F172A]">{getQ(3, 'title') || "משכנתא קיימת"}</h2>
        <p className="text-sm sm:text-base text-[#64748B] mt-2">{getQ(3, 'subtitle') || "האם קיימת משכנתא על הנכס? מה היתרה לסילוק?"}</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="text-sm sm:text-base text-[#475569] mb-2 block">יתרת משכנתא (הזינו 0 אם אין)</Label>
          <div className="relative">
            <Input
              type="text"
              inputMode="numeric"
              placeholder="0"
              value={formatNumber(formData.mortage)}
              onChange={(e) => updateField("mortage", e.target.value.replace(/[^\d]/g, ""))}
              className="h-12 sm:h-14 text-lg sm:text-xl text-center pr-4 pl-12 font-mono"
              data-testid="mortage-input"
            />
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg sm:text-xl text-[#64748B]">₪</span>
          </div>
        </div>

        <div>
          <Label className="text-sm sm:text-base text-[#475569] mb-2 block">החזר חודשי על המשכנתא</Label>
          <div className="relative">
            <Input
              type="text"
              inputMode="numeric"
              placeholder="0"
              value={formatNumber(formData.mortage_monthly)}
              onChange={(e) => updateField("mortage_monthly", e.target.value.replace(/[^\d]/g, ""))}
              className="h-12 sm:h-14 text-lg sm:text-xl text-center pr-4 pl-12 font-mono"
              data-testid="mortage-monthly-input"
            />
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg sm:text-xl text-[#64748B]">₪</span>
          </div>
        </div>
      </div>
    </motion.div>
  );

  // Step 4: Total Debts
  const renderStep4 = () => (
    <motion.div
      key="step4"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4 sm:space-y-6"
      data-testid="step-4"
    >
      <div className="text-center mb-6">
        <Wallet className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-[#C5A45A] mb-3" />
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0F172A]">{getQ(4, 'title') || "סך ההלוואות לאיחוד"}</h2>
        <p className="text-sm sm:text-base text-[#64748B] mt-2">{getQ(4, 'subtitle') || "כולל הלוואות בנקאיות, כרטיסי אשראי, הלוואות לרכב וכו'"}</p>
        <p className="text-xs sm:text-sm text-[#94A3B8] mt-1">{getQ(4, 'hint') || "(לא כולל המשכנתא)"}</p>
      </div>

      <div className="relative">
        <Input
          type="text"
          inputMode="numeric"
          placeholder="לדוגמה: 400,000"
          value={formatNumber(formData.loans)}
          onChange={(e) => updateField("loans", e.target.value.replace(/[^\d]/g, ""))}
          className="h-14 sm:h-16 text-xl sm:text-2xl text-center pr-4 pl-12 font-mono"
          data-testid="loans-input"
        />
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl sm:text-2xl text-[#64748B]">₪</span>
      </div>

      <p className="text-xs sm:text-sm text-[#10B981] text-center">
        רוב הלקוחות שלנו מאחדים בין 300,000 ל-800,000 ₪
      </p>
    </motion.div>
  );

  // Step 5: Current Monthly Payment
  const renderStep5 = () => (
    <motion.div
      key="step5"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4 sm:space-y-6"
      data-testid="step-5"
    >
      <div className="text-center mb-6">
        <Calculator className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-[#C5A45A] mb-3" />
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0F172A]">{getQ(5, 'title') || "ההחזר החודשי הנוכחי"}</h2>
        <p className="text-sm sm:text-base text-[#64748B] mt-2">{getQ(5, 'subtitle') || "כמה אתם משלמים בכל חודש על ההלוואות האלו?"}</p>
        <p className="text-xs sm:text-sm text-[#94A3B8] mt-1">{getQ(5, 'hint') || "(לא כולל החזר משכנתא)"}</p>
      </div>

      <div className="relative">
        <Input
          type="text"
          inputMode="numeric"
          placeholder="לדוגמה: 12,000"
          value={formatNumber(formData.loans_monthly)}
          onChange={(e) => updateField("loans_monthly", e.target.value.replace(/[^\d]/g, ""))}
          className="h-14 sm:h-16 text-xl sm:text-2xl text-center pr-4 pl-12 font-mono"
          data-testid="loans-monthly-input"
        />
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl sm:text-2xl text-[#64748B]">₪</span>
      </div>

      {parseNumber(formData.loans_monthly) > 5000 && (
        <motion.p 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }}
          className="text-xs sm:text-sm text-[#C5A45A] text-center font-medium"
        >
          אנחנו מזהים פוטנציאל חיסכון גבוה...
        </motion.p>
      )}
    </motion.div>
  );

  // Step 6: BDI
  const renderStep6 = () => (
    <motion.div
      key="step6"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4 sm:space-y-6"
      data-testid="step-6"
    >
      <div className="text-center mb-6">
        <Shield className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-[#C5A45A] mb-3" />
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0F172A]">{getQ(6, 'title') || "היסטוריית אשראי"}</h2>
        <p className="text-sm sm:text-base text-[#64748B] mt-2 px-2">{getQ(6, 'subtitle') || "איך הייתם מגדירים את היסטוריית האשראי שלכם ב-3 השנים האחרונות?"}</p>
      </div>

      <RadioGroup
        value={formData.bdi}
        onValueChange={(val) => updateField("bdi", val)}
        className="space-y-2 sm:space-y-3"
      >
        <label 
          className={`flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
            formData.bdi === "excellent" ? "border-[#10B981] bg-[#10B981]/10" : "border-[#E2E8F0] hover:border-[#10B981]/50"
          }`}
        >
          <RadioGroupItem value="excellent" id="bdi-excellent" data-testid="bdi-excellent" />
          <div>
            <span className="font-semibold text-sm sm:text-base text-[#0F172A]">הכל תקין</span>
            <p className="text-xs sm:text-sm text-[#64748B]">מעולם לא חזרו חיובים או הוראות קבע</p>
          </div>
        </label>

        <label 
          className={`flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
            formData.bdi === "medium" ? "border-[#F59E0B] bg-[#F59E0B]/10" : "border-[#E2E8F0] hover:border-[#F59E0B]/50"
          }`}
        >
          <RadioGroupItem value="medium" id="bdi-medium" data-testid="bdi-medium" />
          <div>
            <span className="font-semibold text-sm sm:text-base text-[#0F172A]">היו חריגות קטנות</span>
            <p className="text-xs sm:text-sm text-[#64748B]">היו בעבר חריגות או הוראות קבע שחזרו, אבל הסדרנו</p>
          </div>
        </label>

        <label 
          className={`flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
            formData.bdi === "poor" ? "border-[#EF4444] bg-[#EF4444]/10" : "border-[#E2E8F0] hover:border-[#EF4444]/50"
          }`}
        >
          <RadioGroupItem value="poor" id="bdi-poor" data-testid="bdi-poor" />
          <div>
            <span className="font-semibold text-sm sm:text-base text-[#0F172A]">מצב מורכב</span>
            <p className="text-xs sm:text-sm text-[#64748B]">חשבון מוגבל / תיקים בהוצאה לפועל / צ'קים חוזרים</p>
          </div>
        </label>
      </RadioGroup>

      <p className="text-xs text-[#94A3B8] text-center">
        אנחנו רואים מקרים כאלה כל יום - יש פתרון!
      </p>
    </motion.div>
  );

  // Step 7: Income
  const renderStep7 = () => (
    <motion.div
      key="step7"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4 sm:space-y-6"
      data-testid="step-7"
    >
      <div className="text-center mb-6">
        <PiggyBank className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-[#C5A45A] mb-3" />
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0F172A]">{getQ(7, 'title') || "הכנסת משק הבית"}</h2>
        <p className="text-sm sm:text-base text-[#64748B] mt-2">{getQ(7, 'subtitle') || "מהי הכנסת הנטו המשותפת של משק הבית?"}</p>
      </div>

      <div className="relative">
        <Input
          type="text"
          inputMode="numeric"
          placeholder="לדוגמה: 25,000"
          value={formatNumber(formData.income)}
          onChange={(e) => updateField("income", e.target.value.replace(/[^\d]/g, ""))}
          className="h-14 sm:h-16 text-xl sm:text-2xl text-center pr-4 pl-12 font-mono"
          data-testid="income-input"
        />
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl sm:text-2xl text-[#64748B]">₪</span>
      </div>

      <p className="text-xs text-[#94A3B8] text-center">
        המידע משמש לבדיקת יכולת החזר בלבד
      </p>
    </motion.div>
  );

  // Step 8: Results Preview + Contact Info + Video
  const renderStep8 = () => (
    <motion.div
      key="step8"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4 sm:space-y-6"
      data-testid="step-8"
    >
      {/* Results Preview */}
      <div className="text-center mb-2">
        <CheckCircle2 className="w-12 h-12 sm:w-14 sm:h-14 mx-auto text-[#10B981] mb-2" />
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0F172A]">
          {getQ(8, 'title') || "מצאנו לכם חיסכון משמעותי!"}
        </h2>
      </div>

      {/* Savings Card */}
      <Card className="border-0 shadow-lg overflow-hidden">
        <CardContent className="p-0">
          <div className="grid grid-cols-2">
            <div className="bg-[#FEF2F2] p-3 sm:p-4 text-center">
              <p className="text-xs text-[#991B1B] font-medium mb-1">היום משלמים</p>
              <p className="text-lg sm:text-2xl font-bold text-[#DC2626]">
                {formatNumber(results.currentPayment)} ₪
              </p>
            </div>
            <div className="bg-[#ECFDF5] p-3 sm:p-4 text-center">
              <p className="text-xs text-[#065F46] font-medium mb-1">אפשר לשלם</p>
              <p className="text-lg sm:text-2xl font-bold text-[#10B981]">
                {formatNumber(results.newPayment)} ₪
              </p>
            </div>
          </div>
          <div className="gold-gradient p-4 sm:p-5 text-center">
            <p className="text-white text-sm mb-1">חיסכון חודשי מוערך</p>
            <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
              {formatNumber(results.monthlySavings)} ₪
            </p>
            <p className="text-white/90 text-xs mt-1">בכל חודש, במשך שנים!</p>
          </div>
        </CardContent>
      </Card>

      {/* CTA Header with Countdown */}
      <div className="bg-[#0F172A] text-white rounded-xl p-4 sm:p-5 text-center">
        <p className="text-base sm:text-lg font-bold mb-2">
          🎁 שיחת ייעוץ חינם עם מומחה מומנטו
        </p>
        <p className="text-xs sm:text-sm text-white/80 mb-3">
          השאירו פרטים ונחזור אליכם תוך 24 שעות לתכנון אישי
        </p>
        
        {/* Countdown Timer */}
        <div className={`${timeLeft > 0 ? "bg-[#DC2626]" : "bg-[#64748B]"} rounded-lg p-3 flex items-center justify-center gap-2 transition-colors`}>
          <Clock className="w-4 h-4 sm:w-5 sm:h-5 animate-pulse" />
          <div className="text-center">
            {timeLeft > 0 ? (
              <>
                <p className="text-xs text-white/90">תוקף ההצעה החינמית:</p>
                <p className="text-xl sm:text-2xl font-bold font-mono tabular-nums">
                  {formatTime(timeLeft)}
                </p>
              </>
            ) : (
              <>
                <p className="text-xs text-white/90">ההצעה החינמית פגה</p>
                <p className="text-sm sm:text-base font-bold">כעת השיחה בעלות 199 ₪</p>
              </>
            )}
          </div>
        </div>
        
        {timeLeft > 0 && (
          <p className="text-xs text-white/70 mt-2">
            לאחר מכן: שיחת ייעוץ בעלות 199 ₪
          </p>
        )}
      </div>

      {/* Contact Form */}
      <div className="space-y-3 sm:space-y-4">
        <div>
          <Label className="text-sm sm:text-base text-[#475569] mb-2 block">שם מלא</Label>
          <Input
            type="text"
            placeholder="השם שלכם"
            value={formData.name}
            onChange={(e) => updateField("name", e.target.value)}
            className="h-12 sm:h-14 text-base sm:text-lg"
            data-testid="name-input"
          />
        </div>

        <div>
          <Label className="text-sm sm:text-base text-[#475569] mb-2 block">טלפון</Label>
          <Input
            type="tel"
            inputMode="tel"
            placeholder="050-0000000"
            value={formData.phone}
            onChange={(e) => updateField("phone", e.target.value)}
            className="h-12 sm:h-14 text-base sm:text-lg"
            data-testid="phone-input"
          />
        </div>

        <div>
          <Label className="text-sm sm:text-base text-[#475569] mb-2 block">אימייל (אופציונלי)</Label>
          <Input
            type="email"
            inputMode="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={(e) => updateField("email", e.target.value)}
            className="h-12 sm:h-14 text-base sm:text-lg"
            data-testid="email-input"
          />
        </div>
      </div>

      {/* Inline Submit Button - ABOVE VIDEO */}
      <Button
        onClick={handleNext}
        className="w-full h-14 sm:h-16 text-base sm:text-lg font-bold bg-[#C5A45A] hover:bg-[#B8953D] text-white shadow-lg animate-pulse-slow"
        data-testid="inline-submit-btn"
      >
        <Flame className="w-5 h-5 ml-2" />
        {timeLeft > 0 ? "אני רוצה שיחה חינם עכשיו" : "שלחו לי שיחת ייעוץ"}
        <ChevronLeft className="w-5 h-5 mr-2" />
      </Button>

      {/* Trust badges */}
      <div className="flex flex-wrap justify-center gap-3 text-xs text-[#64748B]">
        <div className="flex items-center gap-1">
          <Lock className="w-3 h-3 text-[#10B981]" />
          <span>הפרטים שלכם מוגנים</span>
        </div>
        <div className="flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3 text-[#10B981]" />
          <span>ללא התחייבות</span>
        </div>
      </div>

      {/* Video Section - BELOW BUTTON */}
      <div className="pt-2">
        <div className="text-center mb-3">
          <p className="text-sm sm:text-base font-semibold text-[#0F172A]">
            הכירו את אלירן בן דוד
          </p>
          <p className="text-xs sm:text-sm text-[#64748B]">
            מייסד מומנטו משכנתאות - עזר לאלפי משפחות בישראל
          </p>
        </div>
        <div className="relative w-full max-w-[280px] mx-auto rounded-2xl overflow-hidden shadow-lg bg-black" style={{ aspectRatio: '9/16' }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src="https://www.youtube.com/embed/gucvcPy5Ok8?rel=0&modestbranding=1"
            title="אלירן בן דוד - מייסד מומנטו"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </motion.div>
  );

  // Processing Screen
  const renderProcessing = () => (
    <motion.div
      key="processing"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"
      data-testid="processing-screen"
    >
      <div className="text-center p-8">
        <div className="relative w-24 h-24 mx-auto mb-8">
          <motion.div
            className="absolute inset-0 border-4 border-[#C5A45A]/30 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          />
          <motion.div
            className="absolute inset-2 border-4 border-t-[#C5A45A] border-r-transparent border-b-transparent border-l-transparent rounded-full"
            animate={{ rotate: -360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          />
          <Calculator className="absolute inset-0 m-auto w-10 h-10 text-[#C5A45A]" />
        </div>

        <ProcessingText />
      </div>
    </motion.div>
  );

  // Processing text animation
  const ProcessingText = () => {
    const [textIndex, setTextIndex] = useState(0);
    const texts = [
      "מנתחים את נתוני השוק...",
      "בודקים אחוזי מימון מותרים...",
      "מחשבים מסלולי השקעה...",
      "מזהים פוטנציאל חיסכון...",
      "נמצאה התאמה!"
    ];

    useEffect(() => {
      const interval = setInterval(() => {
        setTextIndex(prev => (prev < texts.length - 1 ? prev + 1 : prev));
      }, 700);
      return () => clearInterval(interval);
    }, []);

    return (
      <motion.p
        key={textIndex}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl text-[#0F172A] font-medium"
      >
        {texts[textIndex]}
      </motion.p>
    );
  };

  // Main render
  if (isProcessing) {
    return renderProcessing();
  }

  // Wizard steps
  const stepRenderers = {
    1: renderStep1,
    2: renderStep2,
    3: renderStep3,
    4: renderStep4,
    5: renderStep5,
    6: renderStep6,
    7: renderStep7,
    8: renderStep8,
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col overflow-x-hidden">
      {/* Header */}
      <header className="bg-[#0F172A] py-2 sm:py-3 px-3">
        <div className="max-w-md mx-auto flex items-center justify-center">
          <img src={LOGO_URL} alt="MOMENTO" className="h-8 sm:h-10 object-contain" />
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-[#E2E8F0] py-3 sm:py-4 px-3 sticky top-0 z-10">
        <div className="max-w-md mx-auto">
          <div className="flex justify-between text-xs sm:text-sm text-[#64748B] mb-2">
            <span>שלב {currentStep} מתוך 8</span>
            <span>{Math.round((currentStep / 8) * 100)}%</span>
          </div>
          <Progress value={(currentStep / 8) * 100} className="h-2" />
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 px-3 sm:px-4 py-4 sm:py-8">
        <div className="max-w-md mx-auto">
          <AnimatePresence mode="wait">
            {stepRenderers[currentStep]?.()}
          </AnimatePresence>
        </div>
      </main>

      {/* Navigation */}
      <div className="sticky bottom-0 bg-white border-t border-[#E2E8F0] p-3 sm:p-4">
        <div className="max-w-md mx-auto flex gap-2 sm:gap-3">
          {currentStep > 1 && (
            <Button
              variant="outline"
              onClick={handleBack}
              className="h-12 sm:h-14 px-4 sm:px-6 text-sm sm:text-base"
              data-testid="back-btn"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1" />
              חזרה
            </Button>
          )}
          <Button
            onClick={handleNext}
            className="flex-1 h-12 sm:h-14 text-base sm:text-lg font-bold bg-[#C5A45A] hover:bg-[#B8953D] text-white"
            data-testid="next-btn"
          >
            {currentStep === 8 ? "שלחו לי שיחת ייעוץ חינם" : "המשך"}
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LoanCalculator;
